import {
  ETIQUETA_CLASE_SELLO,
  ETIQUETA_CLASE_SELLO_EN,
  ETIQUETA_HITO,
  ETIQUETA_HITO_EN,
  ETIQUETA_TIPO_INSPECCION,
  ETIQUETA_TIPO_INSPECCION_EN,
  type ContenedorLogistico,
  type LoteDeContenedor,
} from '@cnc/contracts';
import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router';
import {useLogisticaDeExportacion} from '../api/seguimiento';
import {et, tr} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_LOGISTICA as L} from './ayuda-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';

// Logistica por contenedor de una exportacion (Etapa 9): hitos, inspeccion de
// la Policia, sellos, peso del cargue (ITR) contra el de llegada a la
// refineria, contenido por proveedor y BL. Viene de "Seguimiento Concentrados".
export function LogisticaDeExportacion({codigo}: {codigo: string}) {
  const consulta = useLogisticaDeExportacion(codigo);
  const x = consulta.data;
  const vacia = x && !x.contenedores.length && !x.sinContenedor.length && !x.bls.length;
  const {hash} = useLocation();
  const seccion = useRef<HTMLDivElement>(null);
  // Desde Seguimiento → Contenedores se llega con #logistica: baja hasta aqui cuando cargan los datos.
  useEffect(() => {
    if (hash === '#logistica' && x) seccion.current?.scrollIntoView({block: 'start'});
  }, [hash, x]);

  return (
    <div
      ref={seccion}
      id="logistica"
      className="scroll-mt-4 rounded-lg border border-linea bg-white p-5"
      data-prueba="logistica"
    >
      <h2 className="font-medium">{tr('Logística por contenedor', 'Container logistics')}</h2>
      <p className="mt-1 text-xs text-apagado">
        {x?.carga
          ? tr(
              `Tomada del archivo de seguimiento de concentrados (${x.carga.archivo ?? 'sin nombre'}) · cargado el ${fechaHora(x.carga.fecha)}`,
              `Taken from the concentrates tracking file (${x.carga.archivo ?? 'unnamed'}) · loaded on ${fechaHora(x.carga.fecha)}`,
            )
          : tr('Tomada del archivo de seguimiento de concentrados.', 'Taken from the concentrates tracking file.')}
      </p>
      <div className="mt-3 space-y-3">
        {consulta.isPending && <p className="text-sm text-apagado">{tr('Consultando…', 'Loading…')}</p>}
        {consulta.isError && <p className="text-sm text-alerta">{consulta.error.message}</p>}
        {vacia && (
          <p className="text-sm text-apagado">
            {tr('Esta exportación todavía no tiene datos de logística.', 'This export has no logistics data yet.')}
          </p>
        )}
        {x && x.bls.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {x.bls.map(b => (
              <span key={b.numero}>
                <span className="text-apagado">
                  BL
                  <NotaDeAyuda titulo="BL" texto={L.bl} />{' '}
                </span>
                <span className="font-medium">{b.numero}</span>
                {b.numeroCorto && b.numeroCorto !== b.numero && (
                  <span className="text-apagado"> ({b.numeroCorto})</span>
                )}
                {b.emitido && (
                  <span className="text-apagado">
                    {' '}
                    · {tr('emitido', 'issued')} {fecha(b.emitido)}
                  </span>
                )}
                {b.switch && <span className="text-apagado"> · switch {fecha(b.switch)}</span>}
              </span>
            ))}
          </div>
        )}
        {x?.contenedores.map(c => (
          <Contenedor key={c.numero} c={c} />
        ))}
        {x && x.sinContenedor.length > 0 && (
          <div className="rounded-md border border-dorado/50 bg-dorado-claro p-3">
            <div className="text-sm font-medium">
              {tr('Filas sin contenedor todavía', 'Rows without a container yet')} ({numero(x.sinContenedor.length)})
              <NotaDeAyuda
                titulo={tr('Filas sin contenedor todavía', 'Rows without a container yet')}
                texto={L.sinContenedor}
              />
            </div>
            <Lotes lotes={x.sinContenedor} />
          </div>
        )}
      </div>
    </div>
  );
}

function Contenedor({c}: {c: ContenedorLogistico}) {
  const hitos = et(ETIQUETA_HITO, ETIQUETA_HITO_EN);
  const clases = et(ETIQUETA_CLASE_SELLO, ETIQUETA_CLASE_SELLO_EN);
  const tipos = et(ETIQUETA_TIPO_INSPECCION, ETIQUETA_TIPO_INSPECCION_EN);
  const bolsasDistintas =
    c.bigBagsEnSeguimiento !== null && c.bigBagsEnApp > 0 && c.bigBagsEnSeguimiento !== c.bigBagsEnApp;
  const revisar = c.lotes.some(l => l.paraRevisar);
  return (
    <details className="rounded-md border border-linea" data-prueba={`contenedor-${c.numero}`}>
      <summary className="flex cursor-pointer flex-wrap items-center gap-x-5 gap-y-1 px-3 py-2 text-sm hover:bg-papel">
        <span className="font-medium">{c.numero}</span>
        {c.tipo && <span className="text-apagado">{c.tipo}</span>}
        <span>
          <span className="text-apagado">
            Big Bags
            <NotaDeAyuda titulo="Big Bags" texto={L.bigBags} />{' '}
          </span>
          <span className="tabular-nums">{numero(c.bigBagsEnSeguimiento ?? c.bigBagsEnApp)}</span>
          {bolsasDistintas && (
            <span className="ml-1 text-xs">
              {tr(`⚠ en la app ${numero(c.bigBagsEnApp)}`, `⚠ ${numero(c.bigBagsEnApp)} in the app`)}
            </span>
          )}
        </span>
        {c.pesoItrKg !== null && (
          <span>
            <span className="text-apagado">
              ITR
              <NotaDeAyuda titulo="ITR" texto={L.itr} />{' '}
            </span>
            <span className="tabular-nums">{numero(c.pesoItrKg)} kg</span>
          </span>
        )}
        {c.pesoRefineriaKg !== null && (
          <span>
            <span className="text-apagado">
              {tr('Refinería', 'Refinery')}
              <NotaDeAyuda titulo={tr('Refinería', 'Refinery')} texto={L.refineria} />{' '}
            </span>
            <span className="tabular-nums">{numero(c.pesoRefineriaKg)} kg</span>
          </span>
        )}
        {c.diferenciaKg !== null && (
          <span
            title={tr(
              'Peso de llegada a la refinería menos peso del cargue (ITR)',
              'Refinery arrival weight minus loading weight (ITR)',
            )}
          >
            <span className="text-apagado">
              {tr('Diferencia', 'Difference')}
              <NotaDeAyuda titulo={tr('Diferencia', 'Difference')} texto={L.diferencia} />{' '}
            </span>
            <span className="font-medium tabular-nums">
              {c.diferenciaKg > 0 ? '+' : ''}
              {numero(c.diferenciaKg)} kg
            </span>
          </span>
        )}
        {c.inspecciones.map((i, n) => (
          <span key={n}>
            <span className="text-apagado">
              {i.autoridad === 'dian'
                ? tr('Selectividad DIAN', 'DIAN selectivity')
                : i.autoridad === 'policia'
                  ? tr('Policía', 'Police')
                  : tr('Inspección', 'Inspection')}
              <NotaDeAyuda titulo={tr('Inspección', 'Inspection')} texto={L.inspeccion} />{' '}
            </span>
            {i.tipo ? tipos[i.tipo] : textoOGuion(i.tipoTexto)}
            {i.fecha && <span className="text-apagado"> {fecha(i.fecha)}</span>}
          </span>
        ))}
        {revisar && <span className="text-xs">{tr('⚠ para revisar', '⚠ to review')}</span>}
      </summary>
      <div className="grid gap-4 border-t border-linea px-3 py-3 md:grid-cols-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
            {tr('Recorrido', 'Journey')}
            <NotaDeAyuda titulo={tr('Recorrido', 'Journey')} texto={L.recorrido} />
          </h3>
          {c.hitos.length === 0 ? (
            <p className="mt-1 text-sm text-apagado">{tr('Sin fechas registradas.', 'No dates recorded.')}</p>
          ) : (
            <ol className="mt-1 space-y-0.5 text-sm">
              {c.hitos.map(h => (
                <li key={h.hito} className="flex justify-between gap-3">
                  <span>
                    {hitos[h.hito]}
                    {h.lugar && <span className="text-apagado"> · {h.lugar}</span>}
                  </span>
                  <span className="tabular-nums">{fecha(h.fecha)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="space-y-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
              {tr('Sellos', 'Seals')}
              <NotaDeAyuda titulo={tr('Sellos', 'Seals')} texto={L.sellos} />
            </h3>
            {c.sellos.length === 0 ? (
              <p className="mt-1 text-sm text-apagado">{tr('Sin sellos registrados.', 'No seals recorded.')}</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-1.5 text-sm">
                {c.sellos.map(s => (
                  <li key={`${s.clase}-${s.numero}`} className="rounded border border-linea px-1.5 py-0.5">
                    <span className="text-xs text-apagado">{clases[s.clase]}: </span>
                    {s.numero}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(c.taraKg !== null || c.numeroAnterior) && (
            <p className="text-sm text-apagado">
              {c.taraKg !== null && tr(`Tara ${numero(c.taraKg)} kg.`, `Tare ${numero(c.taraKg)} kg.`)}{' '}
              {c.numeroAnterior &&
                tr(
                  `En la app estaba como ${c.numeroAnterior}; se corrigió con el seguimiento.`,
                  `It was ${c.numeroAnterior} in the app; corrected from the tracking file.`,
                )}
            </p>
          )}
        </div>
        {c.lotes.length > 0 && (
          <div className="md:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-apagado">
              {tr('Contenido por proveedor', 'Contents by supplier')}
              <NotaDeAyuda titulo={tr('Contenido por proveedor', 'Contents by supplier')} texto={L.contenido} />
            </h3>
            <Lotes lotes={c.lotes} />
          </div>
        )}
      </div>
    </details>
  );
}

function Lotes({lotes}: {lotes: LoteDeContenedor[]}) {
  return (
    <div className="mt-1 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-apagado">
            <th className="py-1 font-semibold">{tr('Proveedor', 'Supplier')}</th>
            <th className="py-1 text-right font-semibold">Big Bags</th>
            <th className="py-1 text-right font-semibold">{tr('Húmedo (kg)', 'Wet (kg)')}</th>
            <th className="py-1 text-right font-semibold">{tr('Seco (kg)', 'Dry (kg)')}</th>
            <th className="py-1 text-right font-semibold">Au (g)</th>
            <th className="py-1 text-right font-semibold">Ag (g)</th>
          </tr>
        </thead>
        <tbody>
          {lotes.map((l, i) => (
            <tr key={`${l.fila ?? 'x'}-${i}`} className="border-t border-linea/70 align-top">
              <td className="py-1">
                {textoOGuion(l.proveedor)}
                {l.paraRevisar && l.nota && <div className="text-xs">⚠ {l.nota}</div>}
              </td>
              <td className="py-1 text-right tabular-nums">{numero(l.bigBags)}</td>
              <td className="py-1 text-right tabular-nums">{numero(l.humedoKg, 2)}</td>
              <td className="py-1 text-right tabular-nums">{numero(l.secoKg, 2)}</td>
              <td className="py-1 text-right tabular-nums">{numero(l.auG, 2)}</td>
              <td className="py-1 text-right tabular-nums">{numero(l.agG, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
